import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
    Activity,
    ArrowRight,
    Cpu,
    CreditCard,
    LogOut,
    Sparkles,
    User as UserIcon,
} from "lucide-react";
import { Reveal, RevealItem } from "../components/Reveal";
import { MagneticButton } from "../components/MagneticButton";
import { useAuthStore } from "../state/authStore";
import { postJSON } from "../lib/api";

function planTone(plan) {
    if (plan === "msp") return { text: "text-accent-mint", bg: "bg-accent-mint/[0.08]", border: "border-accent-mint/30" };
    if (plan === "pro") return { text: "text-accent-cyan", bg: "bg-accent-cyan/[0.08]", border: "border-accent-cyan/30" };
    return { text: "text-text-dim", bg: "bg-elevated/40", border: "border-white/[0.08]" };
}

function intervalLabel(interval) {
    if (interval === "month") return "Billed monthly";
    if (interval === "year") return "Billed annually";
    return null;
}

export const Dashboard = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const welcome = params.get("welcome") === "1";
    const { user, refresh, logout } = useAuthStore();
    const [busy, setBusy] = useState(false);

    if (!user) return null; // <RequireAuth> handles loading

    const plan = user.subscription_plan || "free";
    const status = user.subscription_status || "none";
    const isSubscribed = ["active", "trialing"].includes(status) && ["pro", "msp"].includes(plan);
    const tone = planTone(plan);

    const openPortal = async () => {
        setBusy(true);
        try {
            const r = await postJSON("/billing/portal", {});
            window.location.href = r.url;
        } catch (e) {
            console.error(e);
        } finally {
            setBusy(false);
        }
    };

    const signOut = async () => {
        try {
            await logout();
        } finally {
            navigate("/login");
        }
    };

    return (
        <div className="pt-24 md:pt-28 pb-12 mx-auto max-w-7xl px-5 md:px-10" data-testid="dashboard-page">
            {user.is_beta_tester && (
                <Reveal>
                    <div className="mb-6 rounded-xl border border-accent-cyan/30 bg-gradient-to-r from-accent-cyan/[0.06] via-accent-mint/[0.04] to-transparent px-5 py-4 flex items-center justify-between gap-3 flex-wrap"
                        data-testid="beta-tester-banner">
                        <div className="flex items-center gap-3">
                            <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-accent-cyan/15 border border-accent-cyan/40">
                                <Sparkles className="w-4.5 h-4.5 text-accent-cyan" />
                            </span>
                            <div>
                                <p className="font-display font-semibold text-[14.5px] text-text-primary">
                                    🚀 You're a Beta Tester
                                </p>
                                <p className="mt-0.5 text-[12.5px] text-text-muted">
                                    Thanks for shaping Cybersecurity Forefront. Lifetime Pro access enabled — your feedback drives the roadmap.
                                </p>
                            </div>
                        </div>
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-cyan/15 border border-accent-cyan/40 text-accent-cyan text-[10.5px] font-mono uppercase tracking-[0.22em]">
                            BETA · Lifetime Pro
                        </span>
                    </div>
                </Reveal>
            )}
            {welcome && (
                <Reveal>
                    <div
                        className="mb-6 rounded-xl border border-accent-mint/30 bg-accent-mint/[0.06] px-4 py-3 text-[13.5px] text-accent-mint flex items-center gap-2"
                        data-testid="dashboard-welcome-banner"
                    >
                        <Sparkles className="w-4 h-4" />
                        Welcome aboard. Your subscription is now active.
                    </div>
                </Reveal>
            )}

            <Reveal>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                            Dashboard
                        </p>
                        <h1
                            className="mt-4 font-display font-semibold text-3xl sm:text-4xl tracking-tight leading-[1.05]"
                            data-testid="dashboard-title"
                        >
                            Welcome, <span className="text-accent-cyan">{user.name || user.email}</span>.
                        </h1>
                    </div>
                    <button
                        type="button"
                        onClick={signOut}
                        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md border border-white/[0.08] text-[12.5px] text-text-muted hover:text-accent-red hover:border-accent-red/40 transition-colors duration-200"
                        data-testid="dashboard-signout-btn"
                    >
                        <LogOut className="w-3.5 h-3.5" />
                        Sign out
                    </button>
                </div>
            </Reveal>

            <Reveal stagger className="mt-10 grid gap-5 md:grid-cols-12">
                {/* Account card */}
                <RevealItem className="md:col-span-4 rounded-2xl border border-white/[0.06] bg-surface/60 backdrop-blur-md p-6">
                    <div className="flex items-center gap-3">
                        {user.picture ? (
                            <img
                                src={user.picture}
                                alt=""
                                className="h-12 w-12 rounded-full border border-white/[0.08] object-cover"
                                draggable={false}
                            />
                        ) : (
                            <span className="h-12 w-12 inline-flex items-center justify-center rounded-full bg-accent-cyan/10 border border-accent-cyan/30 text-accent-cyan">
                                <UserIcon className="w-5 h-5" />
                            </span>
                        )}
                        <div className="min-w-0">
                            <p className="text-[14px] font-semibold text-text-primary truncate" data-testid="dashboard-user-name">
                                {user.name}
                            </p>
                            <p className="text-[12px] text-text-muted truncate" data-testid="dashboard-user-email">
                                {user.email}
                            </p>
                        </div>
                    </div>
                    <div className="mt-5 flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-text-dim">
                        <span>Auth</span>
                        <span className="text-text-muted">·</span>
                        <span data-testid="dashboard-auth-provider">{user.auth_provider}</span>
                    </div>
                </RevealItem>

                {/* Subscription card */}
                <RevealItem className="md:col-span-4 rounded-2xl border border-white/[0.06] bg-surface/60 backdrop-blur-md p-6" data-testid="dashboard-subscription-card">
                    <div className="flex items-center justify-between">
                        <p className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim">
                            Subscription
                        </p>
                        <span
                            className={`text-[10.5px] font-mono uppercase tracking-[0.22em] px-2 py-0.5 rounded-md border ${tone.border} ${tone.text} ${tone.bg}`}
                            data-testid="dashboard-plan-badge"
                        >
                            {plan}
                        </span>
                    </div>
                    <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                        <span className="font-display font-semibold text-3xl text-text-primary tracking-tight">
                            {plan === "free" ? "Free" : plan === "pro" ? "Pro" : "MSP"}
                        </span>
                        <span className={`text-[12px] ${tone.text}`} data-testid="dashboard-sub-status">{status}</span>
                        {user.is_founding_member && (
                            <span
                                className="inline-flex items-center gap-1 text-[10.5px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded-md border border-accent-mint/30 text-accent-mint bg-accent-mint/[0.08]"
                                title="Locked in the founding-member price"
                                data-testid="dashboard-founding-chip"
                            >
                                <Sparkles className="w-2.5 h-2.5" />
                                Founding Member
                            </span>
                        )}
                        {user.subscription_billing_interval && (
                            <span
                                className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-text-dim"
                                data-testid="dashboard-billing-interval"
                            >
                                {intervalLabel(user.subscription_billing_interval)}
                            </span>
                        )}
                    </div>
                    {user.subscription_current_period_end && (
                        <p className="mt-2 text-[11px] text-text-muted">
                            Renews / ends:{" "}
                            <span className="font-mono">
                                {new Date(user.subscription_current_period_end).toLocaleDateString()}
                            </span>
                        </p>
                    )}
                    <div className="mt-5 flex flex-wrap gap-2">
                        {!isSubscribed ? (
                            <Link
                                to="/pricing"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent-cyan text-canvas text-[12.5px] font-medium hover:shadow-glow-cyan transition-shadow duration-200"
                                data-testid="dashboard-upgrade-btn"
                            >
                                Upgrade
                                <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        ) : (
                            <button
                                type="button"
                                onClick={openPortal}
                                disabled={busy}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/[0.08] text-[12.5px] text-text-primary hover:border-accent-cyan/40 hover:text-accent-cyan transition-colors duration-200 disabled:opacity-40"
                                data-testid="dashboard-manage-billing-btn"
                            >
                                <CreditCard className="w-3.5 h-3.5" />
                                Manage billing
                            </button>
                        )}
                    </div>
                </RevealItem>

                {/* Quick start card */}
                <RevealItem className="md:col-span-4 rounded-2xl border border-white/[0.06] bg-surface/60 backdrop-blur-md p-6 flex flex-col" data-testid="dashboard-quickstart-card">
                    <p className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim">
                        Quick start
                    </p>
                    <Cpu className="w-6 h-6 text-accent-cyan mt-3" />
                    <h3 className="mt-3 text-base font-semibold text-text-primary">
                        ZeroConf ZTP wizard
                    </h3>
                    <p className="mt-2 text-[12.5px] text-text-muted leading-relaxed flex-1">
                        Generate vendor-correct CLI / XML configs for Fortinet, WatchGuard,
                        Zyxel, and pfSense in minutes.
                    </p>
                    {isSubscribed ? (
                        <MagneticButton
                            type="button"
                            onClick={() => navigate("/portal")}
                            className="mt-4 w-full py-2.5 rounded-md bg-accent-cyan text-canvas text-[13px] font-semibold hover:shadow-glow-cyan transition-shadow duration-200"
                            data-testid="dashboard-launch-zeroconf"
                            ariaLabel="Launch the ZeroConf wizard"
                        >
                            Launch ZeroConf →
                        </MagneticButton>
                    ) : (
                        <div className="mt-4">
                            <button
                                type="button"
                                disabled
                                title="Upgrade to Pro to unlock the ZeroConf provisioning wizard"
                                className="w-full py-2.5 rounded-md bg-elevated/40 border border-white/[0.06] text-[13px] text-text-dim cursor-not-allowed"
                                data-testid="dashboard-launch-zeroconf-disabled"
                            >
                                Locked · Upgrade to use
                            </button>
                            <p className="mt-2 text-[11px] text-text-dim">
                                Upgrade to Pro to unlock the wizard.
                            </p>
                        </div>
                    )}
                </RevealItem>

                {/* Recent activity placeholder */}
                <RevealItem className="md:col-span-12 rounded-2xl border border-white/[0.06] bg-surface/40 backdrop-blur-md p-6" data-testid="dashboard-activity-card">
                    <div className="flex items-center justify-between">
                        <p className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim">
                            Recent activity
                        </p>
                        <Activity className="w-4 h-4 text-text-dim" />
                    </div>
                    <div className="mt-6 text-center py-8">
                        <p className="text-[13.5px] text-text-muted">
                            Activity will appear here once you ship your first ZeroConf
                            configuration.
                        </p>
                    </div>
                </RevealItem>
            </Reveal>
        </div>
    );
};

export default Dashboard;
